import { fitTitle, fitDescription } from "@/lib/seo-text";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, faqSchema, legalServiceSchema } from "@/lib/firm";

const FEES = {
  grant: "$3,300",
  admin: "$8,800",
} as const;

const DESCRIPTION =
  "Probate lawyers in Brisbane and across QLD. Applying for probate or letters of administration in the Supreme Court of Queensland from $3,300 incl GST fixed fee. How long probate takes, what it costs and what an executor must do — handled by email and video.";

const FAQS = [
  {
    q: "Do I need a probate solicitor in Brisbane to apply for a grant?",
    a: "You need a solicitor admitted in Queensland, not one in a particular suburb. Probate applications are filed electronically in the Supreme Court of Queensland registry, so an executor in Brisbane deals with us exactly as an executor in Caloundra does — by phone, email and video. The only item that physically travels is the original signed will.",
  },
  {
    q: "What does an executor in QLD actually have to do?",
    a: "Locate and secure the original will, identify and protect the assets, work out whether a grant is needed, publish the notice of intention to apply and serve the Public Trustee, apply to the Supreme Court, then call in the assets, pay the debts and tax, keep proper estate accounts and distribute to the beneficiaries. The executor is personally accountable for those last steps, which is why the accounts matter.",
  },
  {
    q: "When is probate not required in Queensland?",
    a: "Where the only real property was held as joint tenants and it passes by survivorship, and the bank and share balances are small enough that the institutions will release them on an indemnity, no grant may be needed. We tell you that up front rather than applying for a grant you can do without.",
  },
  {
    q: "How much does a probate solicitor cost in Queensland?",
    a: `Our fixed professional fee for a straightforward grant is ${FEES.grant} including GST, plus the Supreme Court filing fee and the published notice, which are set by others and charged at cost. Full administration including the grant is ${FEES.admin} including GST. We do not charge a percentage of the estate.`,
  },
  {
    q: "How long does probate take in the Supreme Court of Queensland?",
    a: "The notice of intention to apply must be published and served on the Public Trustee, and the application cannot be filed until at least 14 days later. On clean paperwork a grant commonly issues six to ten weeks after we start.",
  },
  {
    q: "What if the deceased died interstate or overseas?",
    a: "That does not stop a Queensland grant. Where the deceased left assets here, the Supreme Court of Queensland can make an original grant, and where a grant has already issued in another Australian state, New Zealand or the United Kingdom, it can usually be resealed here instead. We act for executors living in Sydney, Melbourne and abroad.",
  },
  {
    q: "Can you act if I only have a copy of the will?",
    a: "Often, yes. An application to prove a copy needs evidence about the original — how it was lost, who last held it, and that it was not destroyed with an intention to revoke. It is more work than an ordinary application but it is regularly granted.",
  },
  {
    q: "Who applies if there is no will?",
    a: "The application becomes one for letters of administration and the intestacy rules in the Succession Act 1981 (Qld) set who is entitled to apply and who inherits. The process, notices and our fixed fee are the same.",
  },
  {
    q: "What is the difference between probate and letters of administration in QLD?",
    a: "Probate proves a will and confirms the executor named in it. Letters of administration are granted where there is no valid will, or where the named executor has died or will not act, and the court appoints an administrator instead — usually the person with the largest entitlement on intestacy. Both are grants of representation from the Supreme Court of Queensland and both let you deal with the assets.",
  },
  {
    q: "Do you need a lawyer to apply for probate in Queensland?",
    a: "No — an executor can apply personally. Most who do get a requisition from the registry over the affidavit, the notices or a mark on the will, and lose weeks fixing it. On a fixed fee the arithmetic usually favours having it filed correctly the first time, particularly where there is real property, a copy will, an interstate death or a possible family provision claim.",
  },
];

const EXECUTOR_DUTIES = [
  {
    t: "Secure the original will",
    d: "If another firm, a bank or the Public Trustee holds it in safe custody, we write for its release as your solicitors. Never staple, unpin or write on an original will — a missing pin mark leads to a registry requisition.",
  },
  {
    t: "Work out whether a grant is needed",
    d: "Joint tenancies pass by survivorship. Small balances are often released on an indemnity. We look at the actual asset list before anyone spends money on an application.",
  },
  {
    t: "Publish and serve",
    d: "Notice of intention to apply is published and served on the Public Trustee of Queensland. The 14-day clock starts there, not at the funeral.",
  },
  {
    t: "Protect yourself against claims",
    d: "Family provision applications in Queensland must be notified within six months of death and filed within nine. Distributing early exposes you personally, so we hold the timetable in the file.",
  },
  {
    t: "Call in, pay, account, distribute",
    d: "Banks, shares, superannuation death benefits, insurers, the ATO and Centrelink. Estate accounts are prepared so a beneficiary can see exactly where the money went.",
  },
];

const AREAS = [
  "Brisbane CBD",
  "Northside Brisbane",
  "Southside Brisbane",
  "Redcliffe & Moreton Bay",
  "Ipswich & Logan",
  "Sunshine Coast",
  "Gold Coast",
  "Regional Queensland",
];

const localSchema = {
  "@context": "https://schema.org",
  "@type": ["LegalService", "Attorney"],
  "@id": "https://coastsidelaw.com.au/probate-solicitor-brisbane#service",
  name: `Probate solicitors — Brisbane & Queensland | ${FIRM.name}`,
  description: DESCRIPTION,
  url: "https://coastsidelaw.com.au/probate-solicitor-brisbane",
  telephone: FIRM.phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  areaServed: [
    { "@type": "City", name: "Brisbane" },
    { "@type": "AdministrativeArea", name: "Queensland" },
  ],
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  knowsAbout: [
    "Probate",
    "Letters of administration",
    "Resealing a foreign grant",
    "Executor duties Queensland",
    "Estate administration",
    "Succession Act 1981 (Qld)",
  ],
  makesOffer: [
    {
      "@type": "Offer",
      name: "Grant of probate — Supreme Court of Queensland",
      priceCurrency: "AUD",
      price: "3300",
      description: `${FEES.grant} incl GST fixed professional fee, plus court filing fee and published notice at cost.`,
    },
    {
      "@type": "Offer",
      name: "Full estate administration",
      priceCurrency: "AUD",
      price: "8800",
      description: `${FEES.admin} incl GST all-inclusive fixed fee including the grant.`,
    },
  ],
  provider: legalServiceSchema,
};

export const Route = createFileRoute("/probate-solicitor-brisbane")({
  head: () => ({
    meta: [
      { title: fitTitle(`Probate Lawyers Brisbane & QLD | Fixed Fee ${FEES.grant} Incl GST`) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      {
        property: "og:title",
        content: fitTitle(`Probate lawyers Brisbane & QLD — applying for probate from ${FEES.grant} | ${FIRM.name}`),
      },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/probate-solicitor-brisbane" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/probate-solicitor-brisbane" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: ProbateSolicitorBrisbanePage,
});

function ProbateSolicitorBrisbanePage() {
  return (
    <>
      <PageHero
        eyebrow="Probate lawyers — Brisbane & Queensland"
        title="Probate lawyers in Brisbane and across Queensland"
        lead={`Grants of probate, letters of administration and resealing in the Supreme Court of Queensland. Fixed professional fee of ${FEES.grant} incl GST for a straightforward grant, solicitor-led from start to finish, handled by email and video wherever you live.`}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="#probate-enquiry"
            className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Start a probate enquiry
          </a>
          <a
            href={FIRM.phoneHref}
            className="inline-flex items-center justify-center rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground"
          >
            Call {FIRM.phone}
          </a>
        </div>
      </PageHero>

      <section className="mx-auto max-w-3xl px-5 pt-10">
        <p className="rounded-sm border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground">
          Buying, selling or running a business in Brisbane as well? Our{" "}
          <Link to="/brisbane" className="font-semibold text-primary underline">
            Brisbane hub
          </Link>{" "}
          sets out fixed-fee conveyancing, wills, business and estate work for Brisbane clients,
          with Michael Klein&rsquo;s direct line.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Grant of probate
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{FEES.grant}</p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Fixed professional fee incl GST. Court filing fee and the published notice are set
              by others and charged at cost.
            </p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Full administration
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{FEES.admin}</p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              All-inclusive incl GST, grant included — assets called in, property dealt with,
              estate accounts prepared and the estate distributed.
            </p>
          </div>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          What an executor in Queensland has to do
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Being named executor is a job, not a title. These are the steps we take off your hands,
          and the ones where executors most often get into trouble on their own.
        </p>
        <div className="mt-8 space-y-6">
          {EXECUTOR_DUTIES.map((step) => (
            <div key={step.t} className="border-l-2 border-primary/30 pl-5">
              <h3 className="font-serif text-xl text-foreground">{step.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          How long does probate take in QLD?
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          The notice of intention to apply must be published and served on the Public Trustee of
          Queensland, and the application cannot be filed until at least 14 days after that. On
          clean paperwork the registry commonly issues a grant six to ten weeks after we start.
          What stretches it out is almost always the same short list: an original will that cannot
          be found, a death certificate still with the registry interstate or overseas, an asset
          list that keeps changing, or a requisition over the form of an affidavit. Once the grant
          issues, calling in banks, shares and superannuation usually takes another four to eight
          weeks, and no prudent executor distributes before the nine-month family provision window
          closes.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          How much does probate cost in QLD?
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Our professional fee for a straightforward grant is {FEES.grant} including GST, fixed
          before we start. On top of that sit two disbursements set by others and charged at cost:
          the Supreme Court filing fee, and the fee for publishing the notice of intention to
          apply. Full administration — grant, calling in the assets, dealing with the property,
          estate accounts and distribution — is {FEES.admin} including GST. We do not charge a
          percentage of the estate, so a $400,000 estate and a $4 million estate with the same
          paperwork pay the same fee.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Letters of administration in QLD when there is no will
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Where there is no valid will, or the named executor has died or will not act, the
          application is for letters of administration rather than probate. The intestacy rules in
          the Succession Act 1981 (Qld) decide both who may apply and who inherits — spouse and
          children first, and the shares are fixed by statute, not by what the family agrees was
          intended. The notices, the affidavit material and our fixed fee are the same as a grant
          of probate. Where there is a will but it is only a copy, that is a third path again: an
          application to prove the copy, supported by evidence about how the original was lost.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Why a Brisbane executor can use a coastal firm
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Probate is a Supreme Court of Queensland matter with a single state-wide registry. The
          application, the executor's affidavit, the notices and the registry requisitions are all
          electronic, and identity is verified by video. Where the executor sits makes no
          difference to the outcome — only to the parking. You get a solicitor on the file rather
          than a call centre, and the fee does not move because the estate is larger.
        </p>
        <ul className="mt-6 grid gap-2 sm:grid-cols-2">
          {AREAS.map((a) => (
            <li
              key={a}
              className="rounded-sm border border-border bg-card px-4 py-2 text-sm text-muted-foreground"
            >
              {a}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Interstate, overseas and copy wills
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          A death outside Queensland is not a barrier. Where the deceased left property, bank
          accounts, shares or superannuation here, the Supreme Court of Queensland can make an
          original grant, and a grant already issued in another state, New Zealand or the United
          Kingdom can usually be resealed instead — which is quicker and cheaper. If you hold only
          a copy of the will, an application to prove the copy is regularly granted on the right
          evidence. Tell us what you have in the form below and we will tell you which path
          applies.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Related pages</h2>
        <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
          <li>
            <Link to="/probate-queensland" className="font-semibold text-primary underline">
              Probate in Queensland, online
            </Link>{" "}
            — the full state-wide process, step by step.
          </li>
          <li>
            <Link to="/remote-probate" className="font-semibold text-primary underline">
              Remote probate
            </Link>{" "}
            — how the file runs when you never come to an office.
          </li>
          <li>
            <Link to="/estate-disputes" className="font-semibold text-primary underline">
              Estate disputes
            </Link>{" "}
            — family provision claims and will validity challenges.
          </li>
          <li>
            <Link to="/wills-and-estates" className="font-semibold text-primary underline">
              Wills and estates
            </Link>{" "}
            — fixed-fee wills, EPOAs and estate planning from our two Queensland offices.
          </li>
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <div id="probate-enquiry" className="mt-14 scroll-mt-24">
          <ExecutorIntakeForm heading="Start a probate enquiry" />
        </div>
      </section>

      <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will handle your estate" />

      <CtaBand
        title="Tell us about the estate and we will tell you what it needs"
        body={`Complete the probate enquiry above and we will confirm in writing whether a grant is required and the fixed fee. Or call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
